<template>
  <div class="w-full">
    <div class="grid justify-between grid-cols-12 gap-4">
      <div class="col-start-2 col-end-12">
        <bank-movements-header
          :entity-id="entityId"
          :show-settings-button="isAdmin"
        />
      </div>
      <div
        v-if="accountsRequireLogin"
        class="grid grid-flow-col col-start-2 gap-4"
      >
        <b-tag
          type="is-danger"
        >
          {{ $t('message.accounts.requiresLogin') }}
        </b-tag>
      </div>
      <div class="grid grid-flow-row col-start-2 col-end-6 gap-2 md:grid-flow-col md:gap-4">
        <div v-if="availableBalancesConfiguration">
          <p class="text-xs text-secondary">
            {{ $t('message.table.header.availableBalance') }}
          </p>
          <span class="text-2xl md:text-3xl text-sc-blue">
            {{ accumulatedAvailableBalance | currency('CLP') }}
          </span>
        </div>
        <div v-if="totalBalancesConfiguration">
          <p class="text-xs text-secondary">
            {{ $t('message.table.header.totalBalance') }}
          </p>
          <span class="text-2xl md:text-3xl text-sc-blue">
            {{ accumulatedTotalBalance | currency('CLP') }}
          </span>
        </div>
        <div
          v-if="pendingBalancesConfiguration"
          class="whitespace-nowrap"
        >
          <p class="text-xs text-secondary">
            {{ $t('message.table.header.pendingBalance') }}
          </p>
          <span class="text-2xl md:text-3xl text-sc-blue">
            {{ accumulatedPendingBalance | currency('CLP') }}
          </span>
        </div>
      </div>

      <div class="items-center justify-center hidden col-start-8 col-end-12 md:flex">
        <div class="flex flex-row px-2 py-2 rounded-md bg-sc-blue-lighter">
          <p class="text-xs">
            ¿Quieres conocer más SuperCartola y de cómo se usa? <span class="font-bold">Revisa nuestra documentación</span>
          </p>
          <a
            class="flex h-10 px-2 rounded-md button bg-sc-blue"
            href="/tutorials"
          >
            <inline-svg
              :src="require('@assets/images/document.svg')"
              class="w-6 text-white"
            />
            <div
              class="flex flex-col justify-center ml-2 focus:outline-none"
            >
              <span class="text-sm text-white">Documentación</span>
            </div>
          </a>
        </div>
      </div>

      <bank-movements-filters
        class="col-start-2 col-end-12 pt-6 mb-1 border-t-2"
        :is-refresh-intent-enabled="isRefreshIntentEnabled"
        :show-export-button="isAdmin || isDownloadEnabled"
      />
      <div class="col-start-2 col-end-12">
        <div class="flex justify-end gap-2 md:justify-start">
          <button
            id="approve-selected-button"
            class="px-2 py-1 mb-5 text-sm border rounded text-primary shadow-sc-button disabled:text-disabled disabled:cursor-default"
            @click="toggleConfirmationModalMassiveApproval(false)"
            :disabled="selectedMovementsCount === 0"
            v-if="columnVisible('status')"
          >
            {{ $t('message.table.header.approve') }}
          </button>
          <button
            id="approve-selected-button"
            class="px-2 py-1 mb-5 text-sm border rounded text-primary shadow-sc-button"
            @click="toggleConfirmationModalMassiveApproval(true)"
            v-if="columnVisible('status')"
          >
            {{ $t('message.table.header.approveAll') }}
          </button>
        </div>
        <bank-movements-table
          class="overflow-auto border-t-2 md:border-gray-400"
          :entity-id="entityId"
          :show-plus-button="isAdmin"
          ref="movementTable"
        />
        <div
          class="flex flex-col-reverse items-center gap-4 pb-20 mt-6 md:pb-40 md:flex-row md:gap-0"
          :class="pageOverflow ? 'justify-end': 'justify-between'"
        >
          <div
            v-if="!pageOverflow"
            class="ml-2 text-secondary"
          >
            {{
              $t('message.table.pagination.message',
                 {
                   from: currentPageSmallestIndex,
                   to: currentPageBiggestIndex
                 })
            }}
          </div>
          <div class="flex">
            <button
              id="previous-button"
              class="px-2 py-1 text-sm border rounded text-primary shadow-sc-button disabled:text-disabled disabled:cursor-default"
              :disabled="currentPage === 1"
              @click="previousPage"
            >
              {{ $t('message.table.pagination.previousButton', {}) }}
            </button>
            <button
              class="w-8 h-8 mx-1 text-sm rounded text-primary"
              v-if="currentPage > 2"
              @click="goToPage(1)"
            >
              {{ 1 }}
            </button>
            <p
              v-if="currentPage > 3"
              class="justify-center px-2 m-1 text-sm"
            >
              ...
            </p>
            <button
              v-for="(page, index) in paginationPages"
              :key="index"
              class="w-8 h-8 mx-1 text-sm rounded"
              :class="{
                'bg-sc-blue text-white pointer-events-none': currentPage === page,
                'bg-white text-primary': currentPage !== page,
                'hidden': currentPageMovementsCount === 0 && index !== 0
              }"
              @click="goToPage(page)"
              :disabled="currentPageMovementsCount === 0 && index !== 0"
            >
              {{ page }}
            </button>
            <button
              id="next-button"
              class="px-2 py-1 text-sm border rounded text-primary shadow-sc-button disabled:text-disabled disabled:cursor-default"
              @click="nextPage"
              :disabled="currentPageMovementsCount === 0 || currentPageMovementsCount < pageItems"
            >
              {{ $t('message.table.pagination.nextButton') }}
            </button>
          </div>
        </div>
      </div>
    </div>
    <approve-confirmation-modal
      name="confirmationModalMassiveApproval"
      ref="confirmationModalMassiveApproval"
      :step="approvingAll ? 'all' : 'selected'"
      @confirmation="approveConfirmation"
    />
    <bank-movements-waiting-modal
      v-if="institutionsWaiting"
      ref="bankMovementsWaitingModal"
      :institutions="institutionsWaiting"
    />
  </div>
</template>

<script>
import { camelizeKeys } from 'humps';
import bankMovementsTable from './bank-movements-table.vue';
import bankMovementsFilters from './bank-movements-filters.vue';
import bankMovementsHeader from './bank-movements-header.vue';
import bankMovementsWaitingModal from './bank-movements-waiting-modal.vue';
import approveConfirmationModal from '../approve-confirmation-modal.vue';

export default {
  components: {
    bankMovementsTable,
    bankMovementsFilters,
    bankMovementsHeader,
    bankMovementsWaitingModal,
    approveConfirmationModal,
  },
  data() {
    return {
      approvingAll: false,
      errors: [],
    };
  },
  computed: {
    selectedMovementsCount() {
      return this.$store.state.movements.selectedMovements.length;
    },
    currentPage() {
      return this.$store.state.movements.movementsPage;
    },
    pageItems() {
      return this.$store.state.movements.pageItems;
    },
    currentPageMovementsCount() {
      return this.$store.state.movements.movements.length;
    },
    paginationPages() {
      if (this.currentPage === 1) {
        if (this.currentPageMovementsCount < this.pageItems) {
          return [this.currentPage];
        }

        // eslint-disable-next-line no-magic-numbers
        return [this.currentPage, this.currentPage + 1];
      } else if (this.currentPageMovementsCount < this.pageItems) {
        return [this.currentPage - 1, this.currentPage];
      }

      return [this.currentPage - 1, this.currentPage, this.currentPage + 1];
    },
    pageOverflow() {
      return this.currentPage > this.totalPages;
    },
    accounts() {
      return this.$store.state.movements.accountsFilters;
    },
    showableBalanceAccounts() {
      const showableTypes = ['checking_account', 'savings_account', 'sight_account', 'rut_account'];

      return this.accounts ? Object.values(this.accounts).filter(acc => showableTypes.includes(acc.type)) : [];
    },
    accumulatedAvailableBalance() {
      return this.showableBalanceAccounts ?
        Object.values(this.showableBalanceAccounts)
          .reduce((acc, curr) => (curr.selected && curr.currency === 'CLP' ? acc + curr.availableBalance : acc), 0) : 0;
    },
    accumulatedTotalBalance() {
      return this.showableBalanceAccounts ?
        Object.values(this.showableBalanceAccounts)
          .reduce((acc, curr) => (curr.selected && curr.currency === 'CLP' ? acc + curr.currentBalance : acc), 0) : 0;
    },
    accumulatedPendingBalance() {
      return this.accounts ?
        Object.values(this.accounts)
          .reduce((acc, curr) => (curr.selected && curr.currency === 'CLP' ? acc + curr.pendingBalance : acc), 0) : 0;
    },
    currentPageSmallestIndex() {
      if (this.currentPageMovementsCount === 0) {
        return 0;
      }

      return this.pageItems * (this.currentPage - 1) + (this.currentPageMovementsCount === 0 ? 0 : 1);
    },
    currentPageBiggestIndex() {
      if (this.currentPageMovementsCount === 0) {
        return 0;
      }

      return this.currentPageSmallestIndex + this.currentPageMovementsCount - 1;
    },
    pendingBalancesConfiguration() {
      return this.$store.state.entities.balancesConfiguration.pending;
    },
    totalBalancesConfiguration() {
      return this.$store.state.entities.balancesConfiguration.total;
    },
    availableBalancesConfiguration() {
      return this.$store.state.entities.balancesConfiguration.available;
    },
    accountsRequireLogin() {
      return this.accounts ? Object.values(this.accounts).filter(acc => acc.loginRequired).length > 0 : false;
    },
  },
  watch: {
    accounts(newAccounts) {
      const filteredAccounts = Object.values(newAccounts).filter(account => account.isUpdateable);

      if (this.linksCount > 0 && filteredAccounts.length < 1) {
        this.$buefy.toast.open(
          {
            message: this.$t('message.accounts.needsUpdate'),
            position: 'is-bottom',
          },
        );
      }
    },
  },
  // eslint-disable-next-line max-statements
  async beforeMount() {
    try {
      this.$store.dispatch('setEntityId', this.entityId);
      this.$store.dispatch('getEntityConfiguration');
      this.$store.dispatch('setDefaultFilters', this.userDefaultFilters);
      await this.$store.dispatch('getAccounts');
      this.$store.dispatch('getInitialExtraColumnsFilters');
      this.$store.dispatch('loadDefaultFilters');
      if (window.location.search) {
        this.$store.dispatch('setFiltersFromUrl');
      } else {
        this.$store.dispatch('setFiltersFromStorage');
      }
      this.$store.dispatch('getMovements');
      this.$store.commit('setIsAdmin', this.isAdmin);
      this.$store.dispatch('getLastMovement');
    } catch (error) {
      this.errors.push(error.message);
    }
  },
  mounted() {
    this.$cable.subscribe({
      channel: 'BankMovementsChannel',
      id: this.entityId,
    });
  },
  props: {
    entityId: {
      type: String,
      required: true,
    },
    institutionsWaiting: {
      type: Array,
      default: null,
    },
    isAdmin: {
      type: Boolean,
      required: true,
    },
    isDownloadEnabled: {
      type: Boolean,
      required: true,
    },
    isRefreshIntentEnabled: {
      type: Boolean,
      required: true,
    },
    userDefaultFilters: {
      type: Object,
      default: null,
    },
    linksCount: {
      type: Number,
      required: true,
    },
  },
  channels: {
    BankMovementsChannel: {
      received(data) {
        switch (data.action) {
        case 'get_movements':
          this.getMovements();
          break;
        case 'update_movement':
          this.updateMovement(camelizeKeys(data.bank_movement));
          break;
        default: break;
        }
      },
    },
  },
  methods: {
    columnVisible(columnName) {
      return this.$store.getters.visibleColumn(columnName);
    },
    toggleConfirmationModalMassiveApproval(approvingAll) {
      this.approvingAll = approvingAll;
      this.$refs.confirmationModalMassiveApproval.show();
    },
    approveConfirmation(content) {
      if (this.approvingAll) this.approveAll(content);
      else this.approveAllSelected(content);
    },
    approveAll(content = '') {
      this.$store.dispatch('approveAll', content);
    },
    approveAllSelected(content = '') {
      this.$store.dispatch('approveAllSelected', content);
      this.$refs.movementTable.clearSelectedMovements();
    },
    getMovements() {
      if (this.currentPage === 1) this.$store.dispatch('getMovements');
    },
    updateMovement(movement) {
      this.$store.dispatch('updateMovement', movement);
    },
    nextPage() {
      this.$store.dispatch('nextMovementsPage');
      this.$store.dispatch('getMovements', this.entityId);
      this.addPageToUrl();
    },
    previousPage() {
      this.$store.dispatch('previousMovementsPage');
      this.$store.dispatch('getMovements', this.entityId);
      this.addPageToUrl();
    },
    goToPage(page) {
      this.$store.dispatch('goToMovementsPage', page);
      this.$store.dispatch('getMovements', this.entityId);
      this.addPageToUrl();
    },
    addPageToUrl() {
      const params = new URLSearchParams(window.location.search);
      params.set('page', this.currentPage);
      window.history.replaceState(
        {}, '', `${location.pathname}?${params}`,
      );
    },
  },
};
</script>
